import React, { Component } from 'react';
import { cloneDeep } from 'lodash';
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Upload,
  message,
} from 'antd';
import {
  AudioOutlined,
  CalendarOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  MinusOutlined,
  MobileOutlined,
  PhoneOutlined,
  PlusOutlined,
  StopOutlined,
  UploadOutlined,
  UserOutlined,
} from "@ant-design/icons";
import Resources from '../scripts/resources';
import Rules from '../scripts/rules';
import API from "../scripts/api";
import DatePicker from 'react-modern-calendar-datepicker';
import moment from "moment-jalaali";
import { Constants } from "../scripts/settings";

/* ---------------- helpers ---------------- */

const MAX_RECORD_SECONDS = 300; // حداکثر مدت ضبط: ۵ دقیقه

const RECORD_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
];

const pickRecordMimeType = () => {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
  return RECORD_MIME_CANDIDATES.find(m => MediaRecorder.isTypeSupported(m)) || '';
};

const formatClock = (totalSeconds) => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const isBlob = (v) => typeof Blob !== 'undefined' && v instanceof Blob;

const extFromMime = (mime = '') => {
  if (mime.includes('mp4')) return 'm4a';
  if (mime.includes('ogg')) return 'ogg';
  return 'webm';
};

/**
 * فیلد صوتی: هم آپلود فایل، هم ضبط با میکروفون.
 * مقدار فیلد (value) یکی از این‌هاست:
 *   - File/Blob  => فایل تازه (ضبط‌شده یا انتخاب‌شده) که باید آپلود شود
 *   - string     => آدرس فایل ذخیره‌شده‌ی قبلی (حالت ویرایش)
 *   - undefined  => خالی
 */
class AudioInput extends Component {
  constructor(props) {
    super(props);

    this.state = {
      recording: false,
      seconds: 0,
      previewUrl: '',
    };

    this.mediaRecorder = null;
    this.stream = null;
    this.chunks = [];
    this.timer = null;
    this.isUnmounting = false;
  }

  componentDidMount() {
    this.syncPreviewUrl(this.props.value);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      this.syncPreviewUrl(this.props.value);
    }
  }

  componentWillUnmount() {
    this.isUnmounting = true;
    this.clearTimer();
    this.stopRecorder();
    this.releaseStream();
    this.revokePreviewUrl();
  }

  /* -------- preview -------- */

  syncPreviewUrl = (value) => {
    this.revokePreviewUrl();
    const next = value
      ? (typeof value === 'string' ? value : URL.createObjectURL(value))
      : '';
    this.setState({ previewUrl: next });
  };

  revokePreviewUrl = () => {
    const { previewUrl } = this.state;
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
  };

  /* -------- timer -------- */

  startTimer = () => {
    this.clearTimer();
    this.timer = setInterval(() => {
      this.setState(prev => {
        const seconds = prev.seconds + 1;
        if (seconds >= MAX_RECORD_SECONDS) {
          this.stopRecording();
        }
        return { seconds };
      });
    }, 1000);
  };

  clearTimer = () => {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  };

  /* -------- recording -------- */

  releaseStream = () => {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
  };

  stopRecorder = () => {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        this.mediaRecorder.stop();
      } catch (e) {
        // ignore
      }
    }
  };

  startRecording = async () => {
    if (this.state.recording) return;

    if (!navigator.mediaDevices || typeof MediaRecorder === 'undefined') {
      message.error('مرورگر شما از ضبط صدا پشتیبانی نمی‌کند؛ لطفاً فایل صوتی را آپلود کنید.');
      return;
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      message.error('دسترسی به میکروفون داده نشد. دسترسی مرورگر را بررسی کنید.');
      return;
    }

    const mimeType = pickRecordMimeType();

    try {
      this.mediaRecorder = mimeType
        ? new MediaRecorder(this.stream, { mimeType })
        : new MediaRecorder(this.stream);
    } catch (e) {
      try {
        this.mediaRecorder = new MediaRecorder(this.stream);
      } catch (err) {
        message.error('شروع ضبط صدا ناموفق بود.');
        this.releaseStream();
        return;
      }
    }

    this.chunks = [];
    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) this.chunks.push(event.data);
    };
    this.mediaRecorder.onstop = this.handleRecorderStop;
    this.mediaRecorder.start();

    this.setState({ recording: true, seconds: 0 });
    this.startTimer();
  };

  stopRecording = () => {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.stopRecorder(); // onstop => handleRecorderStop
    } else {
      this.clearTimer();
      this.releaseStream();
      this.setState({ recording: false, seconds: 0 });
    }
  };

  handleRecorderStop = () => {
    const mimeType = (this.mediaRecorder && this.mediaRecorder.mimeType) || 'audio/webm';
    const blob = new Blob(this.chunks, { type: mimeType });
    this.chunks = [];

    this.clearTimer();
    this.releaseStream();
    this.setState({ recording: false, seconds: 0 });

    if (this.isUnmounting) return; // فرم بسته شده، مقداری ست نکن

    if (!blob.size) {
      message.error('ضبط صدا ناموفق بود؛ دوباره تلاش کنید.');
      return;
    }

    const fileName = `voice-${Date.now()}.${extFromMime(mimeType)}`;
    let file = blob;
    try {
      file = new File([blob], fileName, { type: mimeType });
    } catch (e) {
      file = blob; // مرورگرهای قدیمی: همان Blob کافی است
    }

    this.props.onChange(file); // مقدار فیلد فرم
  };

  /* -------- upload -------- */

  handleBeforeUpload = (file) => {
    const isAudio = file.type ? file.type.startsWith('audio/') : true;

    if (!isAudio) {
      message.error('فقط فایل صوتی مجاز است.');
      return false;
    }
    if (file.size > 20 * 1024 * 1024) {
      message.error('حجم فایل صوتی نباید بیشتر از ۲۰ مگابایت باشد.');
      return false;
    }

    this.props.onChange(file); // File
    return false;              // جلوگیری از آپلود خودکار antd
  };

  handleRemove = () => {
    this.props.onChange(undefined);
  };

  render() {
    const { recording, seconds, previewUrl } = this.state;
    const { value, disabled } = this.props;
    const hasValue = !!value;

    return (
      <div>
        <Space wrap>
          <Upload
            accept="audio/*"
            showUploadList={false}
            beforeUpload={this.handleBeforeUpload}
            disabled={disabled || recording}
          >
            <Button icon={<UploadOutlined />} disabled={disabled || recording}>
              انتخاب فایل صوتی
            </Button>
          </Upload>

          {!recording ? (
            <Button
              type="primary"
              icon={<AudioOutlined />}
              onClick={this.startRecording}
              disabled={disabled}
            >
              شروع ضبط
            </Button>
          ) : (
            <Button
              danger
              icon={<StopOutlined />}
              onClick={this.stopRecording}
              disabled={disabled}
            >
              توقف ضبط ({formatClock(seconds)})
            </Button>
          )}

          {hasValue && (
            <Button icon={<DeleteOutlined />} onClick={this.handleRemove} disabled={disabled}>
              حذف فایل
            </Button>
          )}
        </Space>

        {hasValue && (
          <div style={{ marginTop: 8 }}>
            {previewUrl && (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <audio controls src={previewUrl} style={{ width: '100%' }} />
            )}
            {typeof value === 'string' ? (
              <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                فایل صوتی ذخیره‌شده
              </span>
            ) : (
              <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                {value.name || 'صدای ضبط‌شده'} ({(value.size / 1024).toFixed(0)} کیلوبایت)
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
}

/* ---------------- فرم اصلی ---------------- */

class LogisticsScheduleForm extends Component {
  constructor() {
    super();

    this.state = {
      fileList: [],
      countries: [],
      cities: [],
      neighborhoods: [],
      loading: false,
      loadingCities: false,
      loadingNeighborhoods: false,
      editMode: false,
      type: 'text', // نوع پیام: text | audio
    };

    this.formRef = React.createRef();
  }

  /* -------- submit -------- */

  // buildFormData = (values) => {
  //   const formData = new FormData();

  //   Object.keys(values).forEach((key) => {
  //     const val = values[key];
  //     if (val === undefined || val === null) return;

  //     if (key === 'audio' && isBlob(val)) {
  //       const fileName = val.name || `voice-${Date.now()}.${extFromMime(val.type)}`;
  //       formData.append('audio', val, fileName);
  //       return;
  //     }

  //     if (typeof val === 'boolean') {
  //       formData.append(key, val ? 1 : 0);
  //       return;
  //     }

  //     formData.append(key, val);
  //   });

  //   return formData;
  // };


  buildFormData = (values) => {
    const formData = new FormData();

    Object.keys(values).forEach((key) => {
      const val = values[key];
      if (val === undefined || val === null || val === '') return;

      if (key === 'audio') {
        if (isBlob(val)) {
          const fileName = val.name || `voice-${Date.now()}.${extFromMime(val.type)}`;
          formData.append('audio', val, fileName);
        } else if (typeof val === 'string') {
          // در صورت ویرایش و وجود آدرس قبلی
          formData.append('audio', val);
        }
        return;
      }

      if (typeof val === 'boolean') {
        formData.append(key, val ? 1 : 0);
        return;
      }

      formData.append(key, val);
    });

    return formData;
  };

  handleSubmit = () => {
    this.formRef.current
      .validateFields()
      .then(values => {
        if (values.send_date) {
          values.send_date = moment(
            `${values.send_date.year}-${values.send_date.month}-${values.send_date.day}`,
            Constants.jDateFormat
          ).format(Constants.dateFormat);
        }

        if (values.type !== 'audio') {
          // در حالت متنی، فایل صوتی فرستاده نشود
          delete values.audio;
          this.props.onSubmit(values);
          return;
        }

        // حالت صوتی: هم فایل جدید و هم آدرس فایل قبلی، به‌صورت multipart ارسال می‌شود
        this.props.onSubmit(this.buildFormData(values));
      })
      .catch(errorInfo => {
        if (errorInfo.errorFields && errorInfo.errorFields.length > 0) {
          const firstError = errorInfo.errorFields[0].errors[0];
          message.error(firstError || 'لطفاً فیلدهای الزامی را به درستی تکمیل کنید.');
        } else {
          message.error('خطایی در اعتبارسنجی فرم رخ داده است.');
        }
        console.error('Validation Failed:', errorInfo);
      });
  };

  /* -------- lifecycle -------- */

  handleWillUnmount = () => {
    this.formRef.current.resetFields();
    this.setState({
      fileList: [],
      cities: [],
      neighborhoods: [],
      type: 'text',
      editMode: false,
    });
  };

  handleDidMount = async (data = {}) => {
    const cloneData = cloneDeep(data);
    await this.getCountries();

    if (cloneData.country_id) {
      await this.getCities(cloneData.country_id);
    }
    if (cloneData.city_id) {
      await this.getNeighborhoods(cloneData.city_id);
    }
    if (cloneData.send_date) {
      const send_date = moment(cloneData.send_date);
      cloneData.send_date = {
        day: send_date.jDate(),
        month: send_date.jMonth() + 1,
        year: send_date.jYear(),
      };
    }

    this.formRef.current.setFieldsValue(cloneData);

    this.setState({
      editMode: !!cloneData.id,
      type: cloneData.type || 'text',
    });
  };

  /* -------- data -------- */

  getCountries = (params = {}) => {
    this.setState({ loading: true });

    return API.country.list(params)
      .then((result) => {
        this.setState({ loading: false, countries: result.data || [] });
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  };

  getCities = (country_id) => {
    this.setState({ loadingCities: true });

    return API.city.list(country_id)
      .then((result) => {
        this.setState({ loadingCities: false, cities: result.data || [] });
      })
      .catch(() => {
        this.setState({ loadingCities: false });
      });
  };

  getNeighborhoods = (city_id) => {
    this.setState({ loadingNeighborhoods: true });

    return API.neighborhood.list(city_id)
      .then((result) => {
        this.setState({ loadingNeighborhoods: false, neighborhoods: result.data || [] });
      })
      .catch(() => {
        this.setState({ loadingNeighborhoods: false });
      });
  };

  /* -------- handlers -------- */

  handleCountryChange = async (country_id) => {
    this.formRef.current.setFieldsValue({
      city_id: undefined,
      neighborhood_id: undefined,
    });

    this.setState({ cities: [], neighborhoods: [] });

    if (country_id) {
      await this.getCities(country_id);
    }
  };

  handleCityChange = async (city_id) => {
    this.formRef.current.setFieldsValue({ neighborhood_id: undefined });
    this.setState({ neighborhoods: [] });

    if (city_id) {
      await this.getNeighborhoods(city_id);
    }
  };

  handleChangeDate = (date, obj) => {
    const state = {};
    state[obj] = date;
    this.formRef.current.setFieldsValue(state);
  };

  handleTypeChange = (e) => {
    // مقدار audio_path را پاک نمی‌کنیم تا اگر کاربر بین حالت‌ها جابه‌جا شد، فایل از دست نرود
    this.setState({ type: e.target.value });
  };

  render() {
    const { onCancel, submitting, visible } = this.props;
    const {
      editMode,
      countries,
      cities,
      neighborhoods,
      loadingCities,
      loadingNeighborhoods,
      type,
    } = this.state;

    const DatePickerInput = ({ ref }, name) => {
      const date = this.formRef.current ? this.formRef.current.getFieldValue(name) : null;
      return (
        <Input
          value={date ? `${date.year}-${date.month}-${date.day}` : date}
          prefix={<CalendarOutlined />}
          placeholder={Resources.choose}
          ref={ref}
          readOnly
        />
      );
    };

    return (
      <Modal
        title={editMode ? Resources.edit : Resources.add}
        visible={visible}
        onOk={this.handleSubmit}
        onCancel={onCancel}
        okText={Resources.confirm}
        cancelText={Resources.cancel}
        okButtonProps={{ loading: submitting }}
        cancelButtonProps={{ disabled: submitting }}
        width={700}
      >
        <Form ref={this.formRef} layout="vertical">
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                name="country_id"
                label={Resources.country}
                rules={Rules.required}
              >
                <Select placeholder={Resources.choose} onChange={this.handleCountryChange} allowClear>
                  {countries.map(country => (
                    <Select.Option key={country.id} value={country.id}>
                      {country.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="city_id" label={Resources.city} rules={Rules.required}>
                <Select
                  placeholder={Resources.choose}
                  onChange={this.handleCityChange}
                  allowClear
                  disabled={!cities.length && !loadingCities}
                  loading={loadingCities}
                >
                  {cities.map(city => (
                    <Select.Option key={city.id} value={city.id}>
                      {city.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="neighborhood_id" label={Resources.neighborhood}>
                <Select
                  placeholder={Resources.choose}
                  allowClear
                  disabled={!neighborhoods.length && !loadingNeighborhoods}
                  loading={loadingNeighborhoods}
                >
                  {neighborhoods.map(neighborhood => (
                    <Select.Option key={neighborhood.id} value={neighborhood.id}>
                      {neighborhood.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="send_date" label={Resources.date} rules={Rules.required}>
                <DatePicker
                  locale="fa"
                  onChange={(value) => this.handleChangeDate(value, "send_date")}
                  renderInput={(ref) => DatePickerInput(ref, "send_date")}
                  shouldHighlightWeekends
                />
              </Form.Item>
            </Col>

            {/* نوع پیام */}
            <Col xs={24}>
              <Form.Item
                name="type"
                label="نوع پیام"
                rules={Rules.required}
                initialValue="text"
              >
                <Radio.Group onChange={this.handleTypeChange}>
                  <Radio value="text">متن</Radio>
                  <Radio value="audio">پیام صوتی</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>

            {/* فایل صوتی: فقط در حالت audio */}
            {type === 'audio' && (
              <Col xs={24}>
                <Form.Item
                  name="audio"
                  label="فایل صوتی"
                  dependencies={['type']}
                  extra="می‌توانید فایل صوتی را انتخاب کنید یا همین‌جا با میکروفون ضبط کنید."
                  // rules={[
                  //   ({ getFieldValue }) => ({
                  //     validator(_, value) {
                  //       if (getFieldValue('type') !== 'audio') {
                  //         return Promise.resolve();
                  //       }
                  //       if (value) {
                  //         return Promise.resolve();
                  //       }
                  //       return Promise.reject(
                  //         new Error('در حالت پیام صوتی، ضبط یا انتخاب فایل صوتی الزامی است.')
                  //       );
                  //     },
                  //   }),
                  // ]}
                >
                  <AudioInput />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Form>
      </Modal>
    );
  }
}

export default LogisticsScheduleForm;
